/**
 * NotificationsProvider — owns the single Firestore notification subscription
 * for the authenticated user and exposes it via context.
 *
 * Mounted once inside the protected layout so:
 *   - the NavBar bell reads the same live list / unread count, and
 *   - new notifications raise a design-system toast app-wide (regardless of
 *     whether the dropdown is open), matching how chat surfaces messages.
 *
 * The feed carries two scopes (see ./scope.ts): ordinary account items for
 * the NavBar bell and Channel Partner items for the CP Hub bell. Each bell
 * reads — and marks read — only its own scope.
 */
import * as React from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@finmin/auth';
import { useToast } from '@finmin/ui';
import { channelPartnerKeys } from '@/api/channel-partners';
import { useFirebaseNotifications } from './hooks/use-firebase-notifications';
import { getEventMeta } from './event-meta';
import { NotificationsContext, type NotificationsContextValue } from './notifications-context';
import { isChannelPartnerHubPath, shouldToastNotification } from './scope';
import type { AppNotification } from './types';

const countUnread = (items: AppNotification[]) =>
  items.reduce((acc, n) => (n.read ? acc : acc + 1), 0);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Read at toast time without re-subscribing to Firestore on navigation.
  const inHubRef = React.useRef(isChannelPartnerHubPath(pathname));
  React.useEffect(() => {
    inHubRef.current = isChannelPartnerHubPath(pathname);
  }, [pathname]);

  const handleNew = React.useCallback(
    (n: AppNotification) => {
      // A live CP event (approval, agreement update, …) can change what /me
      // and /options report — refresh them so every CP surface follows.
      if (n.event.startsWith('channel_partner_')) {
        void queryClient.invalidateQueries({ queryKey: channelPartnerKeys.me() });
        if (n.event === 'channel_partner_agreement_updated') {
          void queryClient.invalidateQueries({ queryKey: channelPartnerKeys.options() });
        }
      }
      if (!shouldToastNotification(n, inHubRef.current)) return;
      const { variant } = getEventMeta(n.event);
      const title = n.title || 'New notification';
      const body = n.body || undefined;

      switch (variant) {
        case 'success':
          toast.success(title, body);
          break;
        case 'warning':
          toast.warning(title, body);
          break;
        case 'error':
          toast.error(title, body);
          break;
        default:
          toast({ title, description: body });
      }
    },
    [toast, queryClient],
  );

  const { notifications: all, status, markAsRead, markAllAsRead } = useFirebaseNotifications({
    userId: user?.id,
    onNew: handleNew,
  });

  const value = React.useMemo<NotificationsContextValue>(() => {
    const notifications = all.filter((n) => n.scope === 'account');
    const channelPartnerNotifications = all.filter((n) => n.scope === 'channel_partner');
    return {
      notifications,
      unreadCount: countUnread(notifications),
      channelPartnerNotifications,
      channelPartnerUnreadCount: countUnread(channelPartnerNotifications),
      status,
      markAsRead,
      markAllAsRead,
    };
  }, [all, status, markAsRead, markAllAsRead]);

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
